import { useEffect, useCallback, useRef } from "react";
import { useCopilotChat } from "@zapier/ai-toolkit/client";

interface CopilotToolMessage {
  type: "COPILOT_USER_MESSAGE";
  payload: {
    message: string;
    source: string;
  };
}

/**
 * Hook to listen for postMessage events from copilot tool components
 * and inject them into the chat conversation
 */
export function useCopilotToolMessageListener() {
  const copilot = useCopilotChat();

  // Use ref to always access latest copilot without triggering effect re-runs
  const copilotRef = useRef(copilot);
  copilotRef.current = copilot;

  // Memoize the handler to prevent unnecessary event listener churn
  const handleMessage = useCallback((event: MessageEvent) => {
    // Only handle our specific message type
    if (event.data?.type !== "COPILOT_USER_MESSAGE") {
      return;
    }

    const messageData = event.data as CopilotToolMessage;
    const { message, source } = messageData.payload;

    // Access current copilot from ref
    const currentCopilot = copilotRef.current;

    if (!message || !currentCopilot?.setMessages) {
      return;
    }

    // Add message to the chat conversation
    currentCopilot.setMessages((prevMessages) => [
      ...prevMessages,
      {
        role: "user" as const,
        content: message,
        id: `tool-message-${Date.now()}-${source}`,
        createdAt: new Date(),
        parts: [{ type: "text" as const, text: message }],
      },
    ]);

    // Trigger LLM response
    if (currentCopilot.reload) {
      void currentCopilot.reload();
    }
  }, []); // Empty dependency array since we use ref for copilot access

  useEffect(() => {
    // Add event listener
    window.addEventListener("message", handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]); // Only re-run if handleMessage changes (which it won't due to empty deps)
}
